'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface HeroStats {
  badgeText: string;
  communityCount: string;
  avgPayout: string;
  reviewsToday: string;
  activeBrands: string;
  topEarnerName: string;
  topEarnerAmount: string;
  topEarnerReviews: string;
}

const DEFAULTS: HeroStats = {
  badgeText: "India's #1 Review Platform",
  communityCount: '50,000+',
  avgPayout: '₹1,200',
  reviewsToday: '340+',
  activeBrands: '280+',
  topEarnerName: 'Priya S., Mumbai',
  topEarnerAmount: '₹8,450',
  topEarnerReviews: '12',
};

interface HeroSectionProps {
  onCTAClick: () => void;
}

export default function HeroSection({ onCTAClick }: HeroSectionProps) {
  const heroRef = useRef<HTMLElement>(null);
  const [stats, setStats] = useState<HeroStats>(DEFAULTS);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('platform_settings')
      .select('key, value')
      .in('key', [
        'hero_badge_text', 'hero_community_count', 'hero_stat_avg_payout',
        'hero_stat_reviews_today', 'hero_stat_active_brands',
        'hero_top_earner_name', 'hero_top_earner_amount', 'hero_top_earner_reviews',
      ])
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, string> = {};
        data.forEach((r) => { map[r.key] = r.value || ''; });
        setStats({
          badgeText: map.hero_badge_text || DEFAULTS.badgeText,
          communityCount: map.hero_community_count || DEFAULTS.communityCount,
          avgPayout: map.hero_stat_avg_payout || DEFAULTS.avgPayout,
          reviewsToday: map.hero_stat_reviews_today || DEFAULTS.reviewsToday,
          activeBrands: map.hero_stat_active_brands || DEFAULTS.activeBrands,
          topEarnerName: map.hero_top_earner_name || DEFAULTS.topEarnerName,
          topEarnerAmount: map.hero_top_earner_amount || DEFAULTS.topEarnerAmount,
          topEarnerReviews: map.hero_top_earner_reviews || DEFAULTS.topEarnerReviews,
        });
      });
  }, []);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const mx = (clientX / innerWidth - 0.5) * 2;
      const my = (clientY / innerHeight - 0.5) * 2;
      const blobs = hero.querySelectorAll<HTMLElement>('.hero-blob');
      blobs.forEach((blob, i) => {
        const factor = (i + 1) * 10;
        blob.style.transform = `translate(${mx * factor}px, ${my * factor}px)`;
      });
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const floatingStats = [
    { label: 'Avg. Payout', value: stats.avgPayout, icon: 'BanknotesIcon', color: '#FF5722' },
    { label: 'Reviews Today', value: stats.reviewsToday, icon: 'StarIcon', color: '#F59E0B' },
    { label: 'Active Brands', value: stats.activeBrands, icon: 'BuildingStorefrontIcon', color: '#1A237E' },
  ];

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden bg-white"
      style={{ paddingTop: '64px' }}
    >
      <div className="absolute inset-0 dot-grid-bg opacity-60 pointer-events-none" />

      <div className="hero-blob absolute top-[5%] right-[8%] w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,87,34,0.08) 0%, transparent 70%)', filter: 'blur(60px)', transition: 'transform 0.4s ease-out' }} />
      <div className="hero-blob absolute bottom-[10%] left-[5%] w-[250px] h-[250px] sm:w-[400px] sm:h-[400px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(26,35,126,0.06) 0%, transparent 70%)', filter: 'blur(50px)', transition: 'transform 0.6s ease-out' }} />
      <div className="hero-blob absolute top-1/2 left-1/3 w-[300px] h-[300px] sm:w-[600px] sm:h-[600px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.04) 0%, transparent 60%)', filter: 'blur(80px)', transition: 'transform 0.8s ease-out' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7 space-y-6 sm:space-y-8">
            <div className="inline-flex items-center gap-2 bg-primary-container rounded-full px-4 py-2 border border-primary/15">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              <span className="text-xs font-bold text-primary uppercase tracking-widest">{stats.badgeText}</span>
            </div>

            <h1 className="font-heading text-foreground leading-[1.05]" style={{ fontSize: 'clamp(2.4rem, 6vw, 5rem)', fontWeight: 800 }}>
              India's Most{' '}
              <span className="gradient-text block">Trusted Review</span>
              Platform
            </h1>

            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-xl">
              Brands submit products for <strong className="text-foreground font-semibold">honest reviews & testing</strong>. Creators pick what they love and earn{' '}
              <strong className="text-foreground font-semibold">real money via UPI</strong>. Join {stats.communityCount} verified Indians already earning.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onCTAClick}
                className="group inline-flex items-center justify-center gap-2 cta-gradient text-white font-heading font-bold px-8 py-4 rounded-xl hover:opacity-90 transition-all text-base shadow-lg shadow-primary/20"
              >
                Pick Products & Earn
                <Icon name="ArrowRightIcon" size={18} className="text-white group-hover:translate-x-1 transition-transform" />
              </button>
              <Link
                href="/for-brands"
                className="inline-flex items-center justify-center gap-2 border border-border text-foreground font-heading font-semibold px-8 py-4 rounded-xl hover:bg-muted transition-all text-base"
              >
                Submit a Product
                <Icon name="BuildingStorefrontIcon" size={18} className="text-muted-foreground" />
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-4 sm:gap-6 pt-2">
              {[
                { icon: 'ShieldCheckIcon', text: 'AI Quality Check' },
                { icon: 'CurrencyRupeeIcon', text: 'UPI Payouts' },
                { icon: 'DocumentCheckIcon', text: 'GST Invoices' },
              ].map((badge) => (
                <div key={badge.text} className="flex items-center gap-2 text-muted-foreground">
                  <Icon name={badge.icon as 'ShieldCheckIcon'} size={15} className="text-primary" />
                  <span className="text-xs font-medium">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative h-72 sm:h-80 lg:h-[440px]">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-3xl p-5 sm:p-6 shadow-premium border border-border w-56 sm:w-64 z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 cta-gradient rounded-xl flex items-center justify-center shadow-sm shrink-0">
                    <Icon name="TrophyIcon" size={20} className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Top Earner</div>
                    <div className="font-heading font-bold text-foreground text-sm truncate">{stats.topEarnerName}</div>
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <span className="font-heading font-bold text-3xl text-foreground">{stats.topEarnerAmount}</span>
                  <span className="text-xs text-accent font-bold mb-1">+23% ↑</span>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">from {stats.topEarnerReviews} approved reviews</div>
                <div className="mt-3 flex gap-1">
                  {[1,2,3,4,5].map(s => (
                    <Icon key={s} name="StarIcon" size={12} variant="solid" className="star-filled" />
                  ))}
                </div>
              </div>

              {floatingStats.map((stat, i) => {
                const positions = ['top-4 left-2 sm:left-4', 'top-4 right-2 sm:right-4', 'bottom-4 left-1/2 -translate-x-1/2'];
                return (
                  <div
                    key={stat.label}
                    className={`absolute ${positions[i]} bg-white rounded-2xl px-3 sm:px-4 py-3 shadow-premium border border-border`}
                    style={{ animation: `float ${5 + i}s ease-in-out infinite`, animationDelay: `${i * 0.8}s` }}
                  >
                    <div className="flex items-center gap-2 sm:gap-2.5">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat.color}12` }}>
                        <Icon name={stat.icon as 'BanknotesIcon'} size={14} style={{ color: stat.color }} />
                      </div>
                      <div>
                        <div className="font-heading font-bold text-xs sm:text-sm text-foreground">{stat.value}</div>
                        <div className="text-[9px] sm:text-[10px] text-muted-foreground">{stat.label}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, #ffffff)' }} />
    </section>
  );
}